/**
 * Profile photos are shrunk in the browser before upload: phone photos are often
 * 5–10 MB, and api-next takes at most 700 KB (it then crops to 512×512 itself).
 * Drawing to a canvas also applies the EXIF rotation and drops the metadata.
 */
export const MAX_PHOTO_EDGE = 1024;
const MAX_DATA_URL_LENGTH = 900_000; // ≈ 670 KB decoded

/** A photo the browser couldn't prepare; its message is meant for the person. */
export class PhotoError extends Error {}

/** Scales width × height down (never up) so the longer edge fits within `max`. */
export function fitWithin(
    width: number,
    height: number,
    max = MAX_PHOTO_EDGE,
): { width: number; height: number } {
    const scale = Math.min(1, max / Math.max(width, height));
    return {
        width: Math.max(1, Math.round(width * scale)),
        height: Math.max(1, Math.round(height * scale)),
    };
}

/** A JPEG data URL of the photo, at most 1024px on its longer edge. */
export async function shrinkPhoto(file: File): Promise<string> {
    let bitmap: ImageBitmap;
    try {
        bitmap = await createImageBitmap(file, {
            imageOrientation: 'from-image',
        });
    } catch {
        throw new PhotoError(
            "That file isn't a photo we can read. Try a JPEG or PNG.",
        );
    }
    const { width, height } = fitWithin(bitmap.width, bitmap.height);
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d');
    if (!context)
        throw new PhotoError('Your browser could not prepare the photo.');
    context.fillStyle = '#ffffff'; // transparent PNGs get a white background
    context.fillRect(0, 0, width, height);
    context.drawImage(bitmap, 0, 0, width, height);
    bitmap.close();

    for (const quality of [0.85, 0.7, 0.55]) {
        const url = canvas.toDataURL('image/jpeg', quality);
        if (url.length <= MAX_DATA_URL_LENGTH) return url;
    }
    throw new PhotoError(
        'That photo is too detailed to upload. Try another one.',
    );
}
